import { Box, Divider, Grid, IconButton, ListItemIcon, Menu, Typography } from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { INoteRecording, deleteNoteRecordingRequest } from '../../redux/note/noteSlice';
import VoiceRecording from './VoiceRecording';
import StyledMenuItem from '../Common/StyledMenuItem';
import DeleteNoteConfirmation from '../NotesCard/DeleteNoteConfirmation';
import { useAppSelector } from '../../redux/hooks';
import RecordingDisclaimerModal from '../SingleClient/RecordingDisclaimerModal/RecordingDisclaimerModal';
import { toastStyles } from '../../utils/utils';

type ISingleRecordingProps = {
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  recording: INoteRecording;
};

function SingleRecording({ index, isSelected, onSelect, recording }: ISingleRecordingProps) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [deleteModalOpen, setDeleteModalOpen] = React.useState(false);
  const { deleteRecordingLabel, deleteNoteRecordingLoading } = useAppSelector(
    (state) => state.note
  );
  const dispatch = useDispatch();
  const { id: clientId } = useParams<{ id: string }>();
  const coachId = useAppSelector((state) => state.coach.coach.id);
  const [isDisclaimerModalOpen, setIsDisclaimerModalOpen] = useState(false);
  const [isRecordingDownloading, setIsRecordingDownloading] = useState(false);
  const [isDownloadRecording, setIsDownloadRecording] = useState(false);

  const copyToClipboard = (transcription: string) => {
    navigator.clipboard.writeText(transcription);
    toast.success(t('common.copied'), toastStyles.success);
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
    if (
      event.target instanceof HTMLElement &&
      (event.target.tagName === 'DIV' ||
        event.target.tagName === 'IMG' ||
        event.target.tagName === 'SPAN')
    ) {
      onSelect();
    } else {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const downloadRecording = (url: string) => {
    setIsRecordingDownloading(true);
    const link = document.createElement('a');
    link.href = `${process.env.REACT_APP_MEDIA_FILES_BASE_URL}/${url}`;
    link.download = `recording_${index + 1}.wav`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      setIsRecordingDownloading(false);
      setIsDisclaimerModalOpen(false);
    }, 1000);
  };

  const downloadTranscription = (transcription: string) => {
    const blob = new Blob([transcription], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `transcription_${index + 1}.txt`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    setIsDisclaimerModalOpen(false);
  };

  const deleteRecording = () => {
    handleClose();
    setDeleteModalOpen(true);
  };

  const handleDeleteRecording = () => {
    if (!clientId || !coachId) return;
    dispatch(
      deleteNoteRecordingRequest({
        recordingId: recording.id,
        clientId,
        coachId,
      })
    );
  };

  const handleDismiss = () => {
    setDeleteModalOpen(false);
  };

  return (
    <Grid display="flex" gap={1} flexDirection="column" mt={1}>
      <Grid
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ cursor: 'pointer' }}
        onClick={handleClick}
      >
        <Typography variant="bodyBold" color="primary">
          {t('notes.recording')} {index + 1}
        </Typography>
        <IconButton onClick={() => {}}>
          <MoreHorizIcon style={{ color: '#7CAEBE' }} />
        </IconButton>
      </Grid>
      {isSelected && (
        <Grid mt={1} gap="20px" display="flex" flexDirection="column">
          <VoiceRecording message={recording.recording} />
          <Grid display="flex" flexDirection="column" gap="12px">
            <Grid display="flex" flexDirection="row" justifyContent="space-between" pr={1}>
              <Typography variant="subheadBold" color="primary">
                {t('notes.transcription')}
              </Typography>
              <Box
                onClick={() => copyToClipboard(recording.transcription)}
                sx={{ cursor: 'pointer' }}
              >
                <img src="/assets/icons/copy-transcription.svg" alt="copy-transcription" />
              </Box>
            </Grid>
            <Typography variant="bodyRegular" color="primary">
              {recording.transcription}
            </Typography>
          </Grid>
        </Grid>
      )}
      <Divider />
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <StyledMenuItem
          onClick={() => {
            setIsDisclaimerModalOpen(true);
            handleClose();
            setIsDownloadRecording(true);
          }}
        >
          <ListItemIcon>
            <img src="/assets/icons/music-note.svg" alt="music-note" />
          </ListItemIcon>
          <Typography variant="bodyRegular" color="primary">
            {t('notes.downloadRecording')}
          </Typography>
        </StyledMenuItem>

        <StyledMenuItem
          onClick={() => {
            setIsDisclaimerModalOpen(true);
            handleClose();
            setIsDownloadRecording(false);
          }}
        >
          <ListItemIcon>
            <img src="/assets/icons/transcription.svg" alt="transcription" />
          </ListItemIcon>
          <Typography variant="bodyRegular" color="primary">
            {t('notes.downloadTranscription')}
          </Typography>
        </StyledMenuItem>
        <StyledMenuItem onClick={deleteRecording}>
          <ListItemIcon>
            <img src="/assets/icons/trash-bin-red.svg" alt="trash-bin-red" />
          </ListItemIcon>
          <Typography variant="bodyRegular" color="primary">
            {t('notes.deleteRecording')}
          </Typography>
        </StyledMenuItem>
      </Menu>
      <DeleteNoteConfirmation
        handleDelete={handleDeleteRecording}
        handleDismiss={handleDismiss}
        name={deleteRecordingLabel}
        deleteModalOpen={deleteModalOpen}
        isRecording
        deleteLoading={deleteNoteRecordingLoading}
      />
      <RecordingDisclaimerModal
        handleOnClickLeft={() => setIsDisclaimerModalOpen(false)}
        handleOnClickRight={() =>
          isDownloadRecording
            ? downloadRecording(recording.recording)
            : downloadTranscription(recording.transcription)
        }
        content={
          <>
            <Typography variant="bodyRegular" color="primary">
              {t('notes.downloadRecordingDisclaimer.section1')}
            </Typography>
            <Typography variant="bodyRegular" color="primary">
              {`${t(
                isDownloadRecording ? 'notes.recordings' : 'notes.transcription'
              ).toLowerCase()}.`}
            </Typography>
            <br />
            <br />
            <Typography variant="bodyRegular" color="primary">
              {t('notes.downloadRecordingDisclaimer.section2')}
            </Typography>
          </>
        }
        btnTextLeft={t('common.disagree')}
        btnTextRight={t('common.agreeAndDownload')}
        open={isDisclaimerModalOpen}
        btnRightLoading={isRecordingDownloading}
        md={4}
      />
    </Grid>
  );
}

export default SingleRecording;
